// markdown.ts — a deliberately small markdown -> HTML renderer. We author the
// content ourselves, so this only needs to cover what the series actually uses:
// headings, paragraphs, blockquotes, fenced code, and inline emphasis/code/links.
// Built from scratch to match the rest of the site; no markdown dependency.

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inline = (s: string): string => {
  // Order matters: code spans first so their contents aren't re-parsed.
  let out = escapeHtml(s);
  out = out.replace(/`([^`]+)`/g, (_m, code: string) => `<code>${code}</code>`);
  out = out.replace(/\*\*([^*]+)\*\*/g, (_m, b: string) => `<strong>${b}</strong>`);
  out = out.replace(/\*([^*]+)\*/g, (_m, i: string) => `<em>${i}</em>`);
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_m, text: string, href: string) =>
      `<a href="${href}" target="_blank" rel="noreferrer noopener">${text}</a>`,
  );
  return out;
};

export const renderMarkdown = (src: string): string => {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];
  let para: string[] = [];
  let inCode = false;
  let code: string[] = [];

  const flushPara = (): void => {
    if (para.length) {
      html.push(`<p>${inline(para.join(" "))}</p>`);
      para = [];
    }
  };

  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      if (inCode) {
        html.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
        code = [];
        inCode = false;
      } else {
        flushPara();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      code.push(line);
      continue;
    }

    if (line.trim() === "") {
      flushPara();
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushPara();
      const level = heading[1]!.length;
      html.push(`<h${level}>${inline(heading[2]!)}</h${level}>`);
      continue;
    }

    if (line.startsWith("> ")) {
      flushPara();
      html.push(`<blockquote>${inline(line.slice(2))}</blockquote>`);
      continue;
    }

    para.push(line.trim());
  }

  flushPara();
  if (inCode && code.length) {
    html.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
  }
  return html.join("\n");
};
